<template>
  <v-form v-model="form.valid" @submit="addNewLeague">
    <v-overlay v-model="overlay" :opacity="0.75" :absolute="true">
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular
          v-if="loading"
          color="primary"
          indeterminate
          size="26"
          width="4"
        ></v-progress-circular>

        <div v-else-if="!errorMessage">
          <v-icon color="green" size="64">mdi-check</v-icon>
        </div>

        <div v-else>
          <v-icon color="red" size="64">mdi-alert</v-icon>
        </div>

        <div
          class="mt-4 mb-6 text-h6"
          :class="{
            'green--text': loading === false && !errorMessage,
            'red--text': loading === false && errorMessage,
          }"
        >
          {{
            loading
              ? $t("createNewEvent.form.creatingLeague")
              : errorMessage
              ? errorMessage
              : $t("createNewEvent.form.leagueCreated")
          }}
        </div>
      </div>
    </v-overlay>

    <div class="pa-4 px-8">
      <div
        class="d-flex"
        :class="$vuetify.breakpoint.mobile ? 'flex-column' : 'align-center'"
        :style="$vuetify.breakpoint.mobile ? '' : ' gap: 50px;'"
      >
        <div
          class="d-flex flex-column"
          :style="$vuetify.breakpoint.mobile ? '' : 'width: 300px;'"
        >
          <span class="text-subtitle-1">
            {{ $t("selectASport") }}
          </span>
          <v-select
            v-model="form.sport"
            :items="sports"
            class="mb-6"
            :label="$t('selectASport')"
            item-value="id"
            item-text="sport_name"
            :rules="required"
            return-object
            dense
            filled
            hide-details
            solo-inverted
          />
        </div>

        <div
          class="d-flex flex-column"
          :style="$vuetify.breakpoint.mobile ? '' : 'width: 300px;'"
        >
          <span class="text-subtitle-1">
            {{ $t("selectCountry") }}
          </span>

          <v-autocomplete
            v-model="form.country"
            :items="countries"
            :disabled="fetchingCountries"
            :loading="fetchingCountries"
            :label="$t('selectCountry')"
            item-text="category_name"
            item-value="category_id"
            :rules="required"
            dense
            filled
            solo-inverted
            clearable
          >
            <template v-slot:item="data">
              <img
                width="15"
                class="mr-4"
                :src="
                  `https://sga-assets.s3-sa-east-1.amazonaws.com/flags/1x1/${data.item.flag}.svg`
                "
              />
              {{ data.item.category_name }}
            </template>
          </v-autocomplete>
        </div>
      </div>

      <div
        :class="$vuetify.breakpoint.mobile ? '' : 'mt-4'"
        style="max-width: 300px"
      >
        <span class="text-subtitle-1">
          {{ $t("createNewEvent.form.leagueName") }}
        </span>
        <v-text-field
          v-model="form.name"
          solo-inverted
          dense
          hide-details="auto"
          :rules="required"
          :label="$t('createNewEvent.form.leagueName')"
        />
      </div>

      <div class="mt-10">
        <div class="d-flex align-center">
          <span class="text-subtitle-1">
            {{ $t("createNewEvent.form.riskFactor") }}
          </span>
          <div class="ml-2">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" color="primary" small>
                  mdi-information-outline
                </v-icon>
              </template>
              <div
                v-html="$t('createNewEvent.form.riskFactorDescription')"
              ></div>
            </v-tooltip>
          </div>
        </div>
        <v-radio-group v-model="form.risk">
          <v-radio
            v-for="option in riskOptions"
            :key="option.value"
            :label="option.text"
            :value="option.value"
          ></v-radio>
        </v-radio-group>
      </div>
    </div>
    <div class="d-flex justify-end mr-4 pb-4">
      <v-btn text class="mr-2" @click="cancelNewLeagueCreation">
        {{ $t("toCancel") }}
      </v-btn>

      <v-btn
        color="primary"
        :disabled="
          !form.valid ||
            form.risk === null ||
            Object.keys(form.country).length === 0 ||
            form.name === ''
        "
        @click.prevent="addNewLeague"
      >
        {{ $t("toSave") }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import { validate } from "@/util/validate";

export default {
  props: ["sports", "creatingNewLeague", "currentSport"],

  data: (self) => ({
    required: [validate.required],
    form: {
      valid: false,
      sport: {},
      country: {},
      name: "",
      risk: 1,
    },
    loading: false,
    riskOptions: [
      {
        text: self.$t("createNewEvent.form.min"),
        value: 0.01,
      },
      {
        text: self.$t("createNewEvent.form.med"),
        value: 0.5,
      },
      {
        text: self.$t("createNewEvent.form.max"),
        value: 1,
      },
    ],
    overlay: false,
    countries: [],
    fetchingCountries: false,
    errorMessage: "",
  }),

  created() {
    if (this.creatingNewLeague) {
      this.form.sport = this.currentSport;
      if (this.countries.length > 0) {
        return;
      }

      this.fetchCountries();
    }
  },

  watch: {
    creatingNewLeague(val) {
      if (val) {
        this.form.sport = this.currentSport;
        if (this.countries.length > 0) {
          return;
        }

        this.fetchCountries();
      } else {
        this.form = {
          valid: false,
          sport: {},
          country: {},
          name: "",
          risk: 1,
        };
      }
    },
  },

  methods: {
    fetchCountries() {
      if (this.fetchingCountries) {
        return;
      }

      this.fetchingCountries = true;

      this.$axios
        .get(`/${this.$prefix()}/events/listCategories`)
        .then((res) => {
          this.countries = res.data.dados;
        })
        .finally(() => {
          this.fetchingCountries = false;
        });
    },

    addNewLeague() {
      this.loading = true;
      this.overlay = true;

      const body = {
        league_name: this.form.name,
        sport_id: this.form.sport.id,
        country_id: this.form.country,
        weight_league: this.form.risk,
      };

      this.$axios
        .post(
          `/${this.$prefix()}/manualEvents/leagues/addLeague`,
          this.$formdata(body)
        )
        .then((res) => {
          const id = res.data.dados.id;
          this.$emit("addCreatedLeague", {
            league: {
              id,
              league_name: body.league_name,
              sport_id: body.sport_id,
            },

            sport: this.form.sport,
          });
        })
        .catch((err) => {
          this.errorMessage = err.response.data.messages.error;
        })
        .finally(() => {
          this.loading = false;
          setTimeout(() => {
            this.form.name = "";
            this.form.country = "";
            this.form.risk = "";
            this.errorMessage = "";
            this.overlay = false;
            this.cancelNewLeagueCreation();
          }, 1500);
        });
    },

    cancelNewLeagueCreation() {
      this.$emit("cancelNewLeagueCreation");
    },
  },
};
</script>
